import { isSignal, WritableSignal } from '@angular/core';
import { isNullOrUndefined } from 'src/app/shared/utils';
import { VisualContext } from './VisualContext';
import { logger } from '../utils/logger';

const LOGGER = logger('VisualDataSerializer');

//#region Options & Metadata
export type SerializeReadFunction<T, V> = (v: V) => T;
export type SerializeWriteFunction<T, V> = (t: T) => V;

export type SerializationOptions<T, V> =
  { write?: SerializeWriteFunction<T, V>; } &
  (
    { read: SerializeReadFunction<T, V>; } |
    { defaultValue: V; }
  );

export type SerializationOptionsFactory<T, V = any> =
  (s: VisualDataSerializer) => SerializationOptions<T, V>;

const MetadataKey = '[[VisualDataSerializerMetadata]]';

type Metadata = {
  propertyName: string;
  key: string;
  optionsFactory: SerializationOptionsFactory<any, any>;
}

function getMetadata(target: any): Metadata[] {
  let metadatas = [];

  for (let proto = Object.getPrototypeOf(target); 
           proto; 
           proto = Object.getPrototypeOf(proto)) {
    if (Object.hasOwn(proto, MetadataKey))
      metadatas.push(...proto[MetadataKey]);
  }

  return metadatas;
}

/**
 * Annotates the property to be serialized by {@link VisualDataSerializer}.
 * Note, where a {@link SerializationOptionsFactory} is used - its output
 * is cached after the first call. */
export function Serialize<T, V = any>(options?: SerializationOptions<T, V>);
export function Serialize<T, V = any>(key?: string, options?: SerializationOptions<T, V>);
export function Serialize<T, V = any>(optionsFactory?: SerializationOptionsFactory<T, V>);
export function Serialize<T, V = any>(key?: string, optionsFactory?: SerializationOptionsFactory<T, V>);
export function Serialize(k?: any, o?: any) {
  return function (target: any, propertyName: string) {
    let [key, optionsFactory] =
      (typeof k === 'string')
        ? [k, o]
        : [propertyName, k];

    if (!optionsFactory) {
      optionsFactory = (s => ({
        propertyName,
        key
      }));
    }
    else if (optionsFactory && typeof optionsFactory !== 'function') {
      let optionsValue = optionsFactory;
      optionsFactory = (s => optionsValue);
    }
    
    if (!Object.hasOwn(target, MetadataKey))
      Object.defineProperty(target, MetadataKey, { value: [] });

    target[MetadataKey].push({ propertyName, key, optionsFactory });
  };
}
//#endregion

export class VisualDataSerializer {
  constructor(
    public readonly context: VisualContext,
    public readonly data: Record<string, any>,
    public readonly deserializing: boolean) { }

  /**
   * Serializes/deserializes keys of the given object annotated with {@link Serialize}.
   **/
  serializeAnnotated(target: Object) {
    let metadata = getMetadata(target);
    for (let m of metadata) {
      let v: any = target[m.propertyName];
      let o = m.optionsFactory(this);

      if (isSignal(v) && 'set' in v) {
        this.signal<any, any>(<any>v, m.key, o);
      }
      else {
        target[m.propertyName] = this.serialize(v, m.key, o);
      }
    }
  }

  /**
   * Serializes/deserializes value of the given signal,
   * depending on the mode of the serializer.
   **/
  signal<T, V = any>(
    signal: WritableSignal<T>,
    key: string,
    options?: SerializationOptions<T, V>): void {
    if (this.deserializing) {
      signal.set(this.serialize(undefined, key, options));
    }
    else {
      this.serialize(signal(), key, options);
    }
  }


  serialize<T, V = any>(
    value: T,
    key: string,
    options?: SerializationOptions<T, V>): T {
    if (this.deserializing) {
      let v: any = this.data[key];

      if (!isNullOrUndefined(v))
        v = JSON.parse(v);

      if (options) {
        if ('read' in options) {
          v = options.read(v);
        }
        else {
          if (isNullOrUndefined(v))
            v = options.defaultValue;
        }
      }

      return v;
    }
    else {
      let v: any = value;

      if (options) {
        if (typeof options.write === 'function')
          v = options.write(v);
      }

      this.data[key] = JSON.stringify(v);

      return value;
    }
  }

  read<T>(key: string, defaultValue?: T) {
    let value = this.data[key];
    if (isNullOrUndefined(value))
      return defaultValue;

    try {
      return JSON.parse(value);
    }
    catch (err) {
      LOGGER.warn(`Failed to deserialize '${key}' setting`, { cause: value });
      return defaultValue;
    }
  }

  write<T extends string | number>(key: string, value?: T) {
    this.data[key] = value ? JSON.stringify(value) : null;
  }


  static fromTo(context: VisualContext, from: Object, to: Object) {
    let s = new VisualDataSerializer(context, {}, false);
    s.serializeAnnotated(from);

    let d = new VisualDataSerializer(context, s.data, true);
    d.serializeAnnotated(to);
  }
}
